import { useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';

import { RootState } from '../../store';
import { setRowData } from '../../store/slices/counsellingSlice';
import { useCslnAuthMutation } from './counselling.services';
import { alertService } from '../../shared/services/alertService';

/**
 * Migrated from Angular `FrontPageComponent`
 * (src/app/views/pages/counselling/front-page).
 *
 * Counselling landing/menu screen: a grid of role-gated cards that route to
 * different counselling workflows, plus a (currently dormant) authentication
 * dialog for "Conduct Counselling".
 *
 * Role source (Angular -> Redux session):
 *   TknUserType  -> usertypeid
 *   TknUserType1 -> emisUsertype1
 */

const FrontPage = () => {
  const s = useSelector((state: RootState) => state.session);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Angular compared against both '5' and 5 / 22 loosely. Normalise to strings
  // once and compare against string literals so the behaviour matches.
  const usertypeid = String(s.usertypeid ?? '');
  const emisUsertype1 = String(s.emisUsertype1 ?? '');

  const [cslnAuth] = useCslnAuthMutation();

  // ── Auth dialog state ──────────────────────────────────────────────────────
  const [authenticateDialog, setAuthenticateDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [isShowPwrd, setIsShowPwrd] = useState(true);

  // ── Role-based card visibility (Angular *ngIf, preserved) ──────────────────

  const canUpdateOrderCopy = useMemo(
    () =>
      usertypeid !== '5' ||
      (usertypeid === '5' && emisUsertype1 === '7') ||
      (usertypeid === '5' && emisUsertype1 === '8'),
    [usertypeid, emisUsertype1]
  );

  const canConductCounselling = useMemo(
    () =>
      (usertypeid !== '5' && emisUsertype1 !== '3') ||
      (usertypeid === '5' && emisUsertype1 === '8'),
    [usertypeid, emisUsertype1]
  );

  const canAddVacancies = useMemo(
    () =>
      (usertypeid !== '5' ||
        (usertypeid === '5' && emisUsertype1 === '6') ||
        (usertypeid === '5' && emisUsertype1 === '8') ||
        (usertypeid === '5' && emisUsertype1 === '3')) &&
      ((usertypeid !== '6' && emisUsertype1 !== '1') ||
        (usertypeid === '9' && emisUsertype1 === '1')),
    [usertypeid, emisUsertype1]
  );

  const canAddCandidateList = useMemo(
    () =>
      (usertypeid !== '5' && usertypeid !== '22') ||
      (usertypeid === '5' && emisUsertype1 === '6') ||
      (usertypeid === '5' && emisUsertype1 === '8') ||
      (usertypeid === '5' && emisUsertype1 === '3'),
    [usertypeid, emisUsertype1]
  );

  // ── Navigation handlers ────────────────────────────────────────────────────

  const handleCheckCandidate = useCallback(() => {
    navigate('/counselling/dee?path=candidate');
  }, [navigate]);

  const handleCheckVacancy = useCallback(() => {
    navigate('/counselling/dee?path=vacancy');
  }, [navigate]);

  const handleUpdateOrderCopy = useCallback(() => {
    navigate('/counselling/dee?path=order-copy');
  }, [navigate]);

  const handleConductCounselling = useCallback(() => {
    // Angular: authenticationService.rowDataVal('') then navigate.
    // The auth dialog trigger is commented out in Angular, so navigate directly.
    dispatch(setRowData(''));
    navigate('/counselling/dee');
  }, [dispatch, navigate]);

  const handleUpdateVacancies = useCallback(() => {
    navigate('/counselling/vacancy-update');
  }, [navigate]);

  const handleAddCandidateList = useCallback(() => {
    navigate('/counselling/dee?path=add-candidate');
  }, [navigate]);

  // ── Auth dialog handlers (dormant, ported for parity) ──────────────────────

  const eyeClick = useCallback(() => {
    setIsShowPwrd((prev) => !prev);
  }, []);

  const handleCancelBtn = useCallback(() => {
    setAuthenticateDialog(false);
  }, []);

  const handleLoginBtn = useCallback(async () => {
    if (!password) {
      alertService.error('Please enter authenticate password');
      return;
    }

    const result = {
      records: {
        User: s.username ?? '',
        Pass: password,
      },
    };

    try {
      const data: any = await cslnAuth(result).unwrap();
      if (data.dataStatus) {
        navigate('/counselling/dee');
      } else {
        alertService.error('Your authentication password is mismatched');
      }
    } catch {
      alertService.error('Your authentication password is mismatched');
    }
  }, [password, s.username, cslnAuth, navigate]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="front-page main-body-content">
      <div className="row">
        <div className="col-xl-3 mt-4 col-12">
          <div
            className="card shadow bg-body rounded mx-2 my-2"
            onClick={handleCheckCandidate}
          >
            <div className="row">
              <div className="col">Check Candidate List</div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 mt-4 col-12">
          <div
            className="card shadow bg-body rounded mx-2 my-2"
            onClick={handleCheckVacancy}
          >
            <div className="row">
              <div className="col">Check Vacancy</div>
            </div>
          </div>
        </div>

        {canUpdateOrderCopy && (
          <div className="col-xl-3 mt-4 col-12">
            <div
              className="card shadow bg-body rounded mx-2 my-2"
              onClick={handleUpdateOrderCopy}
            >
              <div className="row">
                <div className="col">Update Order Copy Details</div>
              </div>
            </div>
          </div>
        )}

        {canConductCounselling && (
          <div className="col-xl-3 mt-4 col-12">
            <div
              className="card shadow bg-body rounded mx-2 my-2"
              onClick={handleConductCounselling}
            >
              <div className="row">
                <div className="col">Conduct Counselling</div>
              </div>
            </div>
          </div>
        )}

        {canAddVacancies && (
          <div className="col-xl-3 mt-4 col-12">
            <div
              className="card shadow bg-body rounded mx-2 my-2"
              onClick={handleUpdateVacancies}
            >
              <div className="row">
                <div className="col">Add / Update Vacancies</div>
              </div>
            </div>
          </div>
        )}

        {canAddCandidateList && (
          <div className="col-xl-3 mt-4 col-12">
            <div
              className="card shadow bg-body rounded mx-2 my-2"
              onClick={handleAddCandidateList}
            >
              <div className="row">
                <div className="col">Add Candidate List / Update Seniority</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Authentication dialog (dormant — matches Angular) ─────────────── */}
      <Dialog
        header={<b>Authentication</b>}
        visible={authenticateDialog}
        modal
        draggable={false}
        resizable={false}
        appendTo={document.body}
        onHide={handleCancelBtn}
        footer={
          <>
            <Button
              label="Login"
              icon="pi pi-check"
              className="p-button-text"
              onClick={handleLoginBtn}
            />
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text p-button-danger"
              onClick={handleCancelBtn}
            />
          </>
        }
      >
        <div className="row">
          <div className="text-start col-6">
            <label htmlFor="authPassword" className="inMandatory">
              Autheticate Password
            </label>
            <br />
            <span className="p-input-icon-right" style={{ width: '100%' }}>
              <InputText
                id="authPassword"
                type={isShowPwrd ? 'password' : 'text'}
                className="form-control"
                value={password}
                placeholder="Password"
                autoComplete="off"
                onChange={(e) => setPassword(e.target.value)}
              />
              <i
                className={isShowPwrd ? 'pi pi-eye' : 'pi pi-eye-slash'}
                onClick={eyeClick}
              />
            </span>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default FrontPage;
